import React, { useState, useEffect } from "react";
import { Card, Input, Collapse, Typography, Row, Col, Button, Alert, Skeleton, Spin } from "antd";
import { LinkOutlined } from '@ant-design/icons';

import { getComponentMessages } from "../../../services/workFlowService";
import { fetchComponentData, saveComponentToWorkFlow } from "../../../services/workFlowDiagramService";

const { Panel } = Collapse;
const { Title, Text } = Typography;
const { TextArea } = Input;

// #region Constants
const messageNodeFromMessageJson = [
    "message", "invalidFallback1", "invalidFallback2",
    "emptyFallback1", "emptyFallback2",
    "operatorDayHours", "operatorAfterHours"
];
// #endregion

const MessageConfig = () => {
    // #region useState
    const [componentData, setComponentData] = useState();
    const [nodeKey, setNodeKey] = useState("");
    const [componentLine, setComponentLine] = useState(null);
    const [loading, setLoader] = useState(true);
    const [apiCallError, setApiCallError] = useState('');
    const [operatorData, setOperatorData] = useState({});
    const [messageData, setMessageData] = useState([]);
    const [messageStore, setMessageStore] = useState([]);
    // #endregion

    // #region Effects
    // Load component data from backend service on initial mount
    useEffect(() => {
        const loadComponentData = async () => {
            setLoader(true);
            const data = await fetchComponentData(setNodeKey);
            setComponentData(data);
            setOperatorData(data?.operator);
            setMessageData(data?.messages);
            setComponentLine(data?.componentLines);
            if (data?.componentLines === null) {
                setApiCallError("API failed to get component Line");
            }
        };
        loadComponentData();
    }, []);

    // Fetch messages related to UUIDs found in operator and message data
    useEffect(() => {
        const getMessageOfUUIDs = async () => {
            setLoader(true);
            const uuids = new Set();
            if (operatorData) {
                Object.values(operatorData).forEach(value => {
                    if (value && typeof value === 'string') uuids.add(value);
                });
            }
            if (Array.isArray(messageData) && messageData.length > 0) {
                messageData.forEach(msg => {
                    Object.entries(msg).forEach(([key, val]) => {
                        if (messageNodeFromMessageJson.includes(key) && val && typeof val === 'string') {
                            uuids.add(val);
                        }
                    });
                });
            }
            try {
                const response = await getComponentMessages(Array.from(uuids));
                const formattedMessages = response.data.map(msg => ({
                    message_id: msg.id,
                    message_text: msg.text
                }));
                setMessageStore(prev => [...prev, ...formattedMessages]);
            } catch (error) {
                console.log("Error fetching component messages:", error);
            }
            setLoader(false);
        };
        getMessageOfUUIDs();
    }, [componentData]);
    // #endregion

    // #region Utility Functions
    const handleComponentLine = () => {
        const componentDataWithoutLines = { ...componentData };
        delete componentDataWithoutLines?.componentLines;
        saveComponentToWorkFlow(nodeKey, componentDataWithoutLines, componentLine);
    };

    const getMessageTextById = (messageId) => {
        const found = messageStore.find(msg => msg.message_id === messageId);
        return found ? found.message_text : "";
    };

    const renderMessagesCollapse = () => (
        <Collapse accordion bordered={false}>
            {messageData.map((msg, index) => (
                <Panel key={index} header={<Text strong>{msg.code}</Text>}>
                    <Card type="inner" bordered={false}>
                        {Object.entries(msg).map(([key, value]) => {
                            if (!messageNodeFromMessageJson.includes(key) || !value) return null;
                            return (
                                <Row key={key} gutter={[16, 16]} align="top" style={{ marginBottom: 16 }}>
                                    <Col span={6}><Text>{key}</Text></Col>
                                    <Col span={18}>
                                        <TextArea
                                            value={getMessageTextById(value)}
                                            disabled
                                            autoSize={{ minRows: 2, maxRows: 6 }}
                                            style={{ color: "#333", background: "#f0f2f5", borderRadius: 6 }}
                                        />

                                    </Col>
                                </Row>
                            );
                        })}
                    </Card>
                </Panel>
            ))}
        </Collapse>
    );

    const renderOperatorCollapse = () => (
        <Card type="inner" bordered={false}>
            {Object.entries(operatorData).map(([key, value]) => (
                <Row key={key} gutter={[16, 16]} align="top" style={{ marginBottom: 16 }}>
                    <Col span={6}><Text strong>{key}</Text></Col>
                    <Col span={18}>
                        <TextArea
                            value={getMessageTextById(value)}
                            disabled
                            autoSize={{ minRows: 2, maxRows: 6 }}
                            style={{ color: "#333", background: "#f0f2f5", borderRadius: 6 }}
                        />
                    </Col>
                </Row>
            ))}
        </Card>
    );
    // #endregion

    // #region Render
    return (
        <div style={{ padding: 24, background: "#f9fafc", borderRadius: 12 }}>
            <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
                <Col>
                    <Title level={3} style={{ margin: 0, color: '#2b2b2b' }}>🛠️ Message Configuration</Title>
                </Col>
                <Col>
                    <Button type="primary" icon={<LinkOutlined />} onClick={handleComponentLine}>
                        Get line
                    </Button>
                </Col>
            </Row>

            {apiCallError && (
                <Alert
                    message="Error"
                    description={apiCallError}
                    type="error"
                    showIcon
                    style={{ marginBottom: 16 }}
                />
            )}
            {/* {loading ? (
                <Skeleton active paragraph={{ rows: 4 }} style={{ marginBottom: 16 }} />
            ) : ( */}
            <Spin spinning={loading} tip="Component Data Rendering">
                <Collapse accordion bordered={false} style={{ backgroundColor: "#ffffff", borderRadius: 8 }}>
                    {operatorData && (
                        <Panel header="📋 Operator Messages" key="operator">
                            {renderOperatorCollapse()}
                        </Panel>
                    )}

                    {messageData?.length > 0 && (
                        <Panel header="💬 Messages" key="messages">
                            {renderMessagesCollapse()}
                        </Panel>
                    )}
                </Collapse>
            </Spin>
            {/* )} */}
        </div>
    );
    // #endregion
};

export default MessageConfig;
